import { useContext, useState } from "react";
import { UserCog, ShieldCheck, Search, ArrowUpCircle, ArrowDownCircle, Ban, RotateCcw } from "lucide-react";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { useAuthedApi } from "../../hooks/useAuthedApi";
import LoadingState from "../../components/LoadingState";
import { T } from "../../constants/theme";

export default function ManageAdminsPage() {
  const { user, token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const { data: adminsData, loading } = useAuthedApi(
    "/superadmin/admins",
    token,
    [reloadKey],
  );
  const admins = adminsData?.data || [];

  const refresh = () => setReloadKey((k) => k + 1);

  const search = async () => {
    if (query.trim().length < 2) {
      toast("Type at least 2 characters to search", "error");
      return;
    }
    setSearching(true);
    try {
      const res = await apiFetch(
        `/superadmin/users/search?q=${encodeURIComponent(query.trim())}`,
        {},
        { authToken: token },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Search failed");
      setSearchResults(data.data || []);
    } catch (e) {
      toast(e.message || "Search failed", "error");
    } finally {
      setSearching(false);
    }
  };

  const promote = async (targetUser) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/superadmin/admins/${targetUser.id}/promote`,
        { method: "POST" },
        { authToken: token },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Unable to promote user");
      toast(`${targetUser.email} is now an admin`);
      setSearchResults((r) => r.filter((u) => u.id !== targetUser.id));
      refresh();
    } catch (e) {
      toast(e.message || "Failed to promote user", "error");
    } finally {
      setBusy(false);
    }
  };

  const demote = async (admin) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/superadmin/admins/${admin.id}/demote`,
        { method: "POST" },
        { authToken: token },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Unable to demote admin");
      toast(`${admin.email} is now a customer`);
      refresh();
    } catch (e) {
      toast(e.message || "Failed to demote admin", "error");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (admin, is_active) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/superadmin/users/${admin.id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active }),
        },
        { authToken: token },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Unable to update account status");
      toast(is_active ? "Account reactivated" : "Account deactivated");
      refresh();
    } catch (e) {
      toast(e.message || "Failed to update account", "error");
    } finally {
      setBusy(false);
    }
  };

  const cardStyle = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    padding: 20,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    boxShadow: T.shadowSoft,
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 20px 80px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: T.goldLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <UserCog size={24} color={T.gold} />
        </div>
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", color: T.ink, margin: 0 }}>
          Manage Admins
        </h1>
      </div>
      <p style={{ color: T.muted, marginBottom: 32 }}>
        Promote a user to admin, demote an admin back down, or deactivate an account. Only
        superadmins can do this.
      </p>

      <h3 style={{ color: T.ink, display: "flex", alignItems: "center", gap: 8 }}>
        <Search size={18} color={T.gold} />
        Find a user to promote
      </h3>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          placeholder="Search by name or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          style={{
            flex: 1,
            padding: 12,
            borderRadius: 10,
            border: `1px solid ${T.border}`,
            background: T.paper,
            color: T.ink,
            outline: "none",
          }}
        />
        <button
          onClick={search}
          disabled={searching}
          style={{
            background: `linear-gradient(135deg, ${T.gold}, ${T.goldHover})`,
            color: T.white,
            border: "none",
            borderRadius: 10,
            padding: "0 22px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Search
        </button>
      </div>

      {searchResults.length > 0 && (
        <div style={{ display: "grid", gap: 12, marginBottom: 40 }}>
          {searchResults.map((u) => (
            <div key={u.id} style={cardStyle}>
              <div>
                <div style={{ fontWeight: 700, color: T.ink }}>
                  {u.first_name} {u.last_name}
                </div>
                <div style={{ fontSize: 13, color: T.muted }}>
                  {u.email} · currently {u.role}
                </div>
              </div>
              <button
                onClick={() => promote(u)}
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: T.gold,
                  color: T.white,
                  border: "none",
                  borderRadius: 10,
                  padding: "9px 16px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <ArrowUpCircle size={14} />
                Make Admin
              </button>
            </div>
          ))}
        </div>
      )}

      <h3 style={{ color: T.ink, display: "flex", alignItems: "center", gap: 8 }}>
        <ShieldCheck size={18} color={T.gold} />
        Current Admins
      </h3>
      {loading ? (
        <LoadingState />
      ) : admins.length === 0 ? (
        <div style={{ color: T.muted }}>No admins found.</div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {admins.map((a) => {
            const isSuper = a.role === "superadmin";
            const isSelf = a.id === user?.id;
            return (
              <div key={a.id} style={cardStyle}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontWeight: 700, color: T.ink }}>
                      {a.first_name} {a.last_name}
                      {isSelf && <span style={{ color: T.muted }}> (you)</span>}
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: ".04em",
                        textTransform: "uppercase",
                        padding: "3px 10px",
                        borderRadius: 999,
                        color: isSuper ? T.gold : T.ink,
                        background: isSuper ? T.goldLight : T.faint,
                      }}
                    >
                      {a.role}
                    </span>
                    {!a.is_active && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: ".04em",
                          textTransform: "uppercase",
                          padding: "3px 10px",
                          borderRadius: 999,
                          color: "#e07a5f",
                          background: "rgba(224,122,95,0.12)",
                        }}
                      >
                        deactivated
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>{a.email}</div>
                </div>

                {!isSuper && !isSelf && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => demote(a)}
                      disabled={busy}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "transparent",
                        color: T.ink,
                        border: `1px solid ${T.border}`,
                        borderRadius: 10,
                        padding: "9px 16px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <ArrowDownCircle size={14} />
                      Demote
                    </button>
                    {a.is_active ? (
                      <button
                        onClick={() => setStatus(a, false)}
                        disabled={busy}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          background: "transparent",
                          color: "#e07a5f",
                          border: "1px solid #e07a5f",
                          borderRadius: 10,
                          padding: "9px 16px",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        <Ban size={14} />
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(a, true)}
                        disabled={busy}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          background: T.gold,
                          color: T.white,
                          border: "none",
                          borderRadius: 10,
                          padding: "9px 16px",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        <RotateCcw size={14} />
                        Reactivate
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
